<script setup lang="ts">
import FocusModeModal from "@/components/FocusMode/FocusModeModal.vue";
import { FocusImage } from "@/types";
import { defineProps, defineEmits, ref } from "vue";

type Props = {
  input: any;
  step: any;
  steps: any;
  showDetails?: boolean;
  isGroup?: boolean;
  headerClass: string;
  repeatableGroupDetails?: {
    id: number;
    name: string;
    order: number;
  };
};

const props = defineProps<Props>();

type Emits = {
  (event: "addImageModalOpen"): void;
  (event: "refresh"): void;
};

const emits = defineEmits<Emits>();

const hoverStates = ref<any>([]);

const isFocusModeModalOpen = ref(false);
const selectedImage = ref<FocusImage | null>(null);

const toggleFocusModeModal = (image: FocusImage) => {
  selectedImage.value = {
    ...image,
    inputId: props.input.id,
    stepId: props.step.id,
    repeatableGroupIndex: props.input.repeatableGroupIndex,
  };
  isFocusModeModalOpen.value = !isFocusModeModalOpen.value;
};

const onCloseFocusModal = () => {
  isFocusModeModalOpen.value = false;
  selectedImage.value = null;
};
</script>

<template>
  <div>
    <Transition name="fade">
      <FocusModeModal
        v-if="isFocusModeModalOpen && selectedImage"
        :current-step="step"
        :current-input="input"
        :image="selectedImage"
        :steps="steps"
        :repeatable-group-detail="repeatableGroupDetails"
        @on-close="onCloseFocusModal"
        @refresh="
          () => {
            $emit('refresh');
          }
        "
      />
    </Transition>
    <div class="flex flex-col relative">
      <div
        class="w-full flex items-center justify-between bg-[#E5E4E2] min-h-12"
        :class="headerClass"
      >
        <h3
          class="text-[18px] leading-[32px] tracking-[0.01rem] text-primary-text-color font-semibold bg-[#E5E4E2]"
        >
          {{ input.name }}
        </h3>

        <div>
          <img
            @click="() => $emit('addImageModalOpen')"
            class="cursor-pointer"
            src="@/assets/edit-plus-icon.svg"
            alt="Add icon"
          />
        </div>
      </div>
      <div class="relative">
        <div class="gap-[6px] flex flex-wrap w-full">
          <a
            v-for="image in input.value"
            class="w-[300px] no-underline relative rounded-primary-radius"
            :key="image.logId"
            @click="toggleFocusModeModal(image)"
            @mouseenter="() => (hoverStates[image.logId] = true)"
            @mouseleave="() => (hoverStates[image.logId] = false)"
          >
            <div
              class="flex items-center justify-center absolute right-2 bottom-2"
              v-if="image.comment && !hoverStates[image.logId]"
            >
              <img
                src="@/assets/installation-image-comment.svg"
                alt="Comment"
              />
            </div>
            <img
              v-if="!hoverStates[image.logId]"
              class="w-[300px] h-[300px] object-cover rounded-primary-radius"
              :src="image.imageUrl"
              alt="Project image"
              loading="lazy"
            />

            <div
              v-else
              class="bg-[#E5E4E2] w-[300px] h-[300px] p-3 rounded-primary-radius cursor-pointer z-10"
            >
              <img
                class="object-cover rounded-primary-radius mb-2"
                :class="
                  image.comment && hoverStates[image.logId]
                    ? 'w-full h-4/6'
                    : 'w-full h-5/6'
                "
                :src="image.imageUrl"
                alt="Project image"
                loading="lazy"
              />
              <div class="h-1/6 flex flex-col items-start gap-1">
                <p class="text-sm text-primary-text-color font-normal">
                  {{ image.terminName }}
                </p>
                <div class="flex items-center gap-2">
                  <div class="flex items-center gap-1">
                    <img class="w-4 h-4" src="@/assets/calendar-icon.svg" />
                    <p class="text-sm text-primary-text-color font-normal">
                      {{ new Date(image.createdAt).toLocaleDateString("de") }}
                    </p>
                  </div>
                  <div class="flex items-center gap-1">
                    <img src="@/assets/image-user-hover.svg" alt="" />

                    <p
                      v-for="(worker, index) in image.workers"
                      :key="index"
                      class="text-sm text-primary-text-color font-normal"
                    >
                      {{ worker.fullname
                      }}<span v-if="index < image.workers.length - 1">,</span>
                    </p>
                  </div>
                </div>
                <div v-if="image.comment" class="flex items-start gap-1">
                  <img src="@/assets/comment-icon-v2.svg" alt="Comment" />
                  <span
                    class="text-base leading-5 tracking-[0.01rem] text-primary-text-color font-normal"
                    >{{
                      image.comment && image.comment.length > 97
                        ? image.comment.substring(0, 80).trim() + "..."
                        : image.comment
                    }}
                  </span>
                </div>
              </div>
            </div>
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}
</style>
